import { ArrowRight } from './Icons';
import { useTranslation } from '../contexts/TranslationContext';
import { LANDING_SECTION_TITLE_CLASS_NAME, landingSectionClassName } from './landingSectionClassName';
import { localizePublicHref } from '../utils/publicRoutes';

const MANIFESTO_PILLARS = [
  {
    titleKey: 'manifesto.preview.pillars.expertise.title',
    descriptionKey: 'manifesto.preview.pillars.expertise.description',
  },
  {
    titleKey: 'manifesto.preview.pillars.sources.title',
    descriptionKey: 'manifesto.preview.pillars.sources.description',
  },
  {
    titleKey: 'manifesto.preview.pillars.human.title',
    descriptionKey: 'manifesto.preview.pillars.human.description',
  },
];

interface LandingManifestoSectionProps {
  isVisible: boolean;
}

export function LandingManifestoSection({ isVisible }: LandingManifestoSectionProps) {
  const { language, t } = useTranslation();

  return (
    <section
      id="manifesto"
      aria-labelledby="manifesto-preview-title"
      className={landingSectionClassName({
        className: 'overflow-hidden px-4 py-14 sm:px-6 sm:py-32',
      })}
    >
      <div
        id="manifesto-content"
        data-landing-scroll-target
        data-animate
        className={`mx-auto max-w-6xl transition-all duration-1000 motion-reduce:transform-none motion-reduce:transition-none ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}
      >
        <div className="mb-2 w-full text-center sm:mb-3">
          <h2 id="manifesto-preview-title" className={LANDING_SECTION_TITLE_CLASS_NAME}>
            {t('manifesto.eyebrow')}
          </h2>
        </div>
        <div className="grid gap-10 lg:grid-cols-[minmax(0,0.82fr)_minmax(28rem,1.18fr)] lg:items-end lg:gap-16">
          <div className="text-center lg:text-left">
            <p className="mx-auto max-w-3xl text-4xl font-black leading-[1.05] tracking-[-0.04em] text-[rgb(var(--theme-text))] sm:text-5xl md:text-6xl lg:mx-0">
              {t('manifesto.preview.title')}
            </p>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-8 text-[rgb(var(--theme-muted))] sm:text-lg lg:mx-0">
              {t('manifesto.preview.description')}
            </p>
            <a
              href={localizePublicHref('/manifesto', language)}
              className="brand-button brand-button-primary brand-focus-ring mt-8 min-h-12 px-5 py-3 text-sm sm:text-base"
            >
              {t('manifesto.preview.cta')}
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </a>
          </div>

          <div className="grid gap-3">
            {MANIFESTO_PILLARS.map(({ titleKey, descriptionKey }, index) => (
              <article
                key={titleKey}
                data-manifesto-pillar="true"
                className="group grid grid-cols-[5rem_minmax(0,1fr)] items-start gap-4 py-5 first:pt-0 last:pb-0 sm:grid-cols-[5.5rem_minmax(0,1fr)] sm:gap-6 sm:py-6"
              >
                <div className="relative flex min-h-16 items-start justify-center overflow-visible pb-3 sm:min-h-[4.5rem]">
                  <span
                    data-manifesto-pillar-number="true"
                    className="theme-heading-primary inline-block select-none px-2 text-5xl font-black leading-[1.1] tracking-[-0.035em] tabular-nums sm:text-6xl"
                    aria-hidden="true"
                  >
                    {index + 1}
                  </span>
                  <span
                    className="absolute inset-x-2 bottom-0 h-1 rounded-full bg-[linear-gradient(90deg,rgb(var(--theme-primary-strong)),rgb(var(--theme-secondary-strong)))] shadow-[0_5px_16px_rgb(var(--theme-primary)/0.3)]"
                    aria-hidden="true"
                  />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[rgb(var(--theme-text))]">
                    {t(titleKey)}
                  </h3>
                  <p className="mt-2 leading-7 text-[rgb(var(--theme-muted))]">
                    {t(descriptionKey)}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
